'use client'

// Save a canvas asset INTO the library — shared by every capture surface
// (panel drag-drop, the canvas context menu). One place owns the rules:
// normalize bare R2 keys to absolute URLs, preserve media kind, and keep raw
// source URLs separate from optional display posters.

import { getStorageUrl } from '@/lib/api'
import { getAssetDisplayName, getAssetDisplayUri } from '@/lib/assetHelpers'
import { getAssetMediaKind } from '@/lib/assetMediaKind'
import { emitLibrarySavedNotice } from '@/lib/floatingLeftPanelEvents'
import { useBootstrapStore } from '@/lib/multiplayer/bootstrap'
import { trackLibraryAssetsAdded, trackLibraryCollectionCreated, trackLibraryProjectLinkChanged } from '@/lib/analytics'
import {
  uid,
  toSlug,
  toPropertyKey,
  useLibraryStore,
  type AssetKind,
  selectLibraryWriteAccess,
} from '@/store/libraryStore'
import { useStore } from '@/store/yjsStore'
import type { Asset, SubjectCanvasNode } from '@/types'
import type { LibraryCommand } from '@/types/shared'

export type SaveToLibraryResult =
  { ok: true; assetId: string } | { ok: false; reason: 'no-media' | 'no-preview' | 'not-ready' | 'access-denied' }
export type SaveToLibraryFailureReason = Extract<SaveToLibraryResult, { ok: false }>['reason']
export type SaveCanvasAssetsToLibraryResult = {
  collectionId: string | null
  results: SaveToLibraryResult[]
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function libraryOrigin(projectId: string | null, assetId?: string | null, sceneId?: string) {
  if (!projectId || !UUID_RE.test(projectId)) return undefined
  return {
    projectId,
    ...(sceneId ? { sceneId } : {}),
    ...(assetId && UUID_RE.test(assetId) ? { assetId } : {}),
  }
}

/** The name a captured asset carries into the library: the label it had on the
 * canvas AT SAVE TIME — a custom name if the user set one, else the zero-padded
 * shot number ("001"). Prompt → filename are fallbacks for assets with neither
 * (off-board captures). Undefined when nothing readable exists (opaque R2 keys
 * are worse than the caller's fallback). */
export function canvasAssetDisplayName(asset: Asset): string | undefined {
  const custom = getAssetDisplayName(useStore.getState().assetNames, asset)?.slice(0, 60)
  if (custom) return custom
  if (asset.shotNumber != null) return String(asset.shotNumber).padStart(3, '0')
  if (asset.prompt?.trim()) return asset.prompt.trim().slice(0, 60)
  const file = asset.originalFilename ?? decodeURIComponent(asset.uri?.split('?')[0].split('/').pop() ?? '')
  const stem = file.replace(/\.[a-z0-9]{2,5}$/i, '').trim()
  if (!stem || /^[0-9a-f-]{16,}$/i.test(stem)) return undefined
  return stem.slice(0, 60)
}

function canvasAssetLibraryKind(asset: Asset): AssetKind {
  const mediaKind = getAssetMediaKind(asset)
  if (mediaKind === 'file') throw new Error('JSON files cannot be saved to a media collection.')
  return mediaKind === 'set' ? 'model3d' : mediaKind
}

/** Validate whether capture would mutate the library; used before creating target collections. */
export function getCanvasAssetLibrarySaveBlockReason(asset: Asset): SaveToLibraryFailureReason | null {
  if (asset.assetType === 'file' || !asset.uri) return 'no-media'
  if (asset.uri.startsWith('pending:')) return 'not-ready'
  if (asset.uri.startsWith('data:') || asset.uri.startsWith('blob:')) return 'no-media'
  return null
}

/**
 * Capture one user gesture as an ordered Library batch. Collection creation,
 * asset rows, and project enablement are replayed together by the client
 * outbox; the backend applies its idempotent commands sequentially.
 */
export function saveCanvasAssetsToLibrary(
  assets: Asset[],
  options: {
    collectionId?: string | null
    createCollectionName?: string
    enableForProjectId?: string | null
    /** Manual + flow may intentionally create an empty collection. */
    createEmptyCollection?: boolean
  } = {},
): SaveCanvasAssetsToLibraryResult {
  const state = useLibraryStore.getState()
  // Link guests read the project's collections but never write the vault.
  // Ghost read-only sessions get the same refusal: dispatch would silently
  // no-op, and a success result here would show a saved notice (and emit
  // creation analytics) for rows that never exist.
  const writeAccess = selectLibraryWriteAccess(state)
  if (writeAccess !== 'writable' && writeAccess !== 'loading') {
    return {
      collectionId: null,
      results: assets.map(() => ({ ok: false, reason: 'access-denied' as const })),
    }
  }
  if (writeAccess === 'loading') {
    return {
      collectionId: null,
      results: assets.map(() => ({ ok: false, reason: 'not-ready' as const })),
    }
  }

  const commands: LibraryCommand[] = []
  const results: SaveToLibraryResult[] = []
  const now = Date.now()
  let target = options.collectionId ?? null
  let targetCollectionName =
    state.collections.find(collection => collection.id === target && !collection.deletedAt)?.name ?? 'Saved'
  let createdCollection = false
  let createdCollectionSource: 'manual' | 'auto' = 'auto'

  if (options.createCollectionName?.trim()) {
    target = uid()
    createdCollection = true
    createdCollectionSource = 'manual'
    const name = options.createCollectionName.trim()
    targetCollectionName = name
    commands.push({
      type: 'collection.create',
      collection: {
        id: target,
        name,
        slug: toSlug(
          name,
          state.collections.map(collection => collection.slug),
        ),
        folder: null,
        tags: [],
        heroAssetId: null,
        usedIn: 0,
        rev: 1,
        createdAt: now,
        updatedAt: now,
      },
    })
  } else if (!target) {
    const saved = state.collections.find(collection => collection.name === 'Saved' && !collection.deletedAt)
    if (saved) {
      target = saved.id
      targetCollectionName = saved.name
    } else {
      target = uid()
      targetCollectionName = 'Saved'
      createdCollection = true
      commands.push({
        type: 'collection.create',
        collection: {
          id: target,
          name: 'Saved',
          slug: toSlug(
            'Saved',
            state.collections.map(collection => collection.slug),
          ),
          folder: null,
          tags: [],
          heroAssetId: null,
          usedIn: 0,
          rev: 1,
          createdAt: now,
          updatedAt: now,
        },
      })
    }
  }

  const activeProjectId = useBootstrapStore.getState().currentProjectId
  const activeSceneId = useStore.getState().activeSceneId ?? undefined
  let plannedAssetCount = state.assets.filter(asset => asset.collectionId === target && !asset.deletedAt).length
  const createdKinds: AssetKind[] = []

  for (const asset of assets) {
    const blockReason = getCanvasAssetLibrarySaveBlockReason(asset)
    if (blockReason) {
      results.push({ ok: false, reason: blockReason })
      continue
    }

    const kind = canvasAssetLibraryKind(asset)
    const poster = kind === 'image' ? undefined : getAssetDisplayUri(asset)
    const origin = libraryOrigin(activeProjectId, asset.id)
    if (origin && activeSceneId) origin.sceneId = activeSceneId
    const assetId = uid()
    plannedAssetCount += 1
    commands.push({
      type: 'asset.create',
      asset: {
        id: assetId,
        collectionId: target!,
        kind,
        src: getStorageUrl(asset.uri),
        posterSrc: poster ? getStorageUrl(poster) : undefined,
        origin,
        name: canvasAssetDisplayName(asset) ?? `${targetCollectionName} ${plannedAssetCount}`,
        width: asset.width,
        height: asset.height,
        sourceKind: asset.source === 'uploaded' ? 'upload' : 'generation',
        attrs: {},
        rev: 1,
        createdAt: now,
        updatedAt: now,
      },
    })
    createdKinds.push(kind)
    results.push({ ok: true, assetId })
  }

  if ((!createdKinds.length && !options.createEmptyCollection) || !target) {
    return { collectionId: null, results }
  }

  const projectId = options.enableForProjectId
  const currentLinks = projectId ? (state.projectLinks[projectId] ?? []) : []
  if (projectId && !currentLinks.includes(target)) {
    commands.push({ type: 'link.setProject', projectId, collectionIds: [...currentLinks, target] })
  }

  state.dispatch({ type: 'batch', label: 'Save to library', commands }, { label: 'Save to library' })
  if (createdKinds.length) {
    trackLibraryAssetsAdded({
      workspaceId: state.workspaceId,
      source: 'canvas',
      count: createdKinds.length,
      imageCount: createdKinds.filter(kind => kind === 'image').length,
      videoCount: createdKinds.filter(kind => kind === 'video').length,
      textCount: 0,
      assetSourceKind: assets.some(asset => asset.source === 'uploaded') ? 'upload' : 'generation',
    })
  }
  if (createdCollection) {
    trackLibraryCollectionCreated({ workspaceId: state.workspaceId, source: createdCollectionSource })
  }
  if (projectId && !currentLinks.includes(target)) {
    trackLibraryProjectLinkChanged({
      workspaceId: state.workspaceId,
      projectId,
      action: 'enable',
      enabledCount: currentLinks.length + 1,
    })
  }
  return { collectionId: target, results }
}

/** collectionId null → the default "Saved" collection (created on demand). */
export function saveCanvasAssetToLibrary(asset: Asset, collectionId: string | null): SaveToLibraryResult {
  return saveCanvasAssetsToLibrary([asset], { collectionId }).results[0] ?? { ok: false, reason: 'no-media' }
}

// --- Old subject system → library ------------------------------------------------
//
// One old canvas subject becomes one library collection: name → collection
// name, description → description, category → tag, main image → first asset
// (the hero fallback), reference images → more assets. An old subject GROUP
// becomes ONE collection (variant = atom): each member's images are variants,
// and members' variantProperties map onto select properties + facet values.
// Non-destructive: the subject stays on the canvas. Voice, poses, meshes and
// character sheets don't migrate — pipeline artifacts with no library home.

export type ImportSubjectResult =
  { ok: true; collectionId: string; imageCount: number } | { ok: false; reason: 'no-images' | 'access-denied' }

/** Importable image URLs a subject contributes: the (cropped) main image
 * first, then reference angles. Local/in-flight URLs are skipped. */
function subjectImageEntries(subject: SubjectCanvasNode): { url: string; assetId?: string | null }[] {
  const entries: { url: string; assetId?: string | null }[] = []
  if (subject.mainImageUrl) entries.push({ url: subject.mainImageUrl, assetId: subject.mainImageAssetId })
  for (const [i, url] of (subject.referenceImageUrls ?? []).entries()) {
    if (url && url !== subject.mainImageUrl) entries.push({ url, assetId: subject.referenceImageAssetIds?.[i] })
  }
  return entries.filter(
    e => e.url && !e.url.startsWith('data:') && !e.url.startsWith('blob:') && !e.url.startsWith('pending:'),
  )
}

/** URLs already living in a collection — the seed for batch-wide dedup. */
function storeSrcsFor(collectionId: string): Set<string> {
  const s = useLibraryStore.getState()
  return new Set(s.assets.filter(a => a.collectionId === collectionId && !a.deletedAt).map(a => a.src))
}

/** Enable collections for a project INSIDE the import batch, so one undo
 * reverses the import AND its project links together. Fires the same
 * analytics as libraryActions.enableCollectionForProject. Call the tracker
 * only after the batch actually dispatches. */
function appendEnableCommand(
  projectId: string,
  collectionIds: string[],
  commands: LibraryCommand[],
): (() => void) | null {
  const s = useLibraryStore.getState()
  const existing = s.projectLinks[projectId] ?? []
  const next = [...new Set([...existing, ...collectionIds])]
  if (next.length === existing.length) return null
  commands.push({ type: 'link.setProject', projectId, collectionIds: next })
  return () =>
    trackLibraryProjectLinkChanged({
      workspaceId: s.workspaceId,
      projectId,
      action: 'enable',
      enabledCount: next.length,
    })
}

/** Commands importing one subject. Appends to `commands`; returns the target
 * collection id (existing id, or the freshly minted one) and how many assets
 * were added. Re-imports are cheap no-ops: images whose URL already lives in
 * the target collection are skipped. */
function buildSubjectImportCommands(
  subject: SubjectCanvasNode,
  opts: {
    intoCollectionId?: string
    folder?: string | null
    /** Batch-wide dedup set (mutated). When several subjects import into the
     * same collection in ONE batch, the store can't see earlier planned
     * assets — pass a shared set so duplicate URLs converge. Defaults to the
     * target collection's live srcs. */
    dedupSrcs?: Set<string>
  },
  commands: LibraryCommand[],
  takenSlugs: string[],
): {
  collectionId: string
  imageCount: number
  created: boolean
  assets: { id: string; role: 'main' | 'angle' }[]
} | null {
  const s = useLibraryStore.getState()
  const entries = subjectImageEntries(subject)
  if (!entries.length) return null
  const now = Date.now()
  const projectId = useBootstrapStore.getState().currentProjectId
  const name = subject.name.trim() || 'Subject'
  const category = subject.category.trim()

  let target = opts.intoCollectionId
  let created = false
  if (!target) {
    target = uid()
    created = true
    const slug = toSlug(name, takenSlugs)
    takenSlugs.push(slug)
    commands.push({
      type: 'collection.create',
      collection: {
        id: target,
        name,
        slug,
        folder: opts.folder ?? null,
        tags: category ? [category] : [],
        heroAssetId: null, // UI falls back to the first live asset = the main image
        description: subject.description?.trim() || undefined,
        usedIn: 0,
        rev: 1,
        createdAt: now,
        updatedAt: now,
      },
    })
    if (category && !s.tagVocab.includes(category)) {
      commands.push({ type: 'tag.addToVocab', tags: [category] })
    }
  }

  const existingSrcs = opts.dedupSrcs ?? storeSrcsFor(target)
  let imageCount = 0
  const assets: { id: string; role: 'main' | 'angle' }[] = []
  for (const [i, entry] of entries.entries()) {
    const url = getStorageUrl(entry.url)
    if (existingSrcs.has(url)) continue
    existingSrcs.add(url)
    imageCount++
    const assetId = uid()
    // entries[0] plays the main-image role even when it came from the refs
    // (subjects without an explicit main use their first image everywhere).
    assets.push({ id: assetId, role: i === 0 ? 'main' : 'angle' })
    commands.push({
      type: 'asset.create',
      asset: {
        id: assetId,
        collectionId: target,
        kind: 'image',
        src: url,
        name: i === 0 ? name : `${name} ${i + 1}`,
        sourceKind: 'import',
        attrs: {},
        origin: libraryOrigin(projectId, entry.assetId),
        rev: 1,
        createdAt: now,
        updatedAt: now,
      },
    })
  }
  return { collectionId: target, imageCount, created, assets }
}

/** Per-import bookkeeping for variantProperties → library properties, so two
 * members sharing a "mood" key create ONE property with merged options. */
type PropertyPlan = {
  byLabel: Map<string, { key: string; options: Map<string, string> }>
  createdCount: number
}

function initPropertyPlan(): PropertyPlan {
  const s = useLibraryStore.getState()
  const byLabel: PropertyPlan['byLabel'] = new Map()
  for (const p of s.properties.filter(p => !p.deletedAt)) {
    byLabel.set(p.label.trim().toLowerCase(), {
      key: p.key,
      options: new Map((p.options ?? []).map(o => [o.value.trim().toLowerCase(), o.id])),
    })
  }
  return { byLabel, createdCount: 0 }
}

/** Get-or-create a select property (by label) and one of its options (by
 * value) inside the current batch; returns what asset.setValue needs. */
function planOption(
  label: string,
  value: string,
  commands: LibraryCommand[],
  plan: PropertyPlan,
): { key: string; optionId: string } {
  const s = useLibraryStore.getState()
  let prop = plan.byLabel.get(label.toLowerCase())
  if (!prop) {
    const key = toPropertyKey(
      label,
      [...plan.byLabel.values()].map(p => p.key),
    )
    prop = { key, options: new Map() }
    plan.byLabel.set(label.toLowerCase(), prop)
    plan.createdCount++
    commands.push({
      type: 'property.create',
      property: {
        id: uid(),
        key,
        label,
        type: 'select',
        options: [],
        sortOrder: s.properties.length + plan.createdCount - 1,
      },
    })
  }
  let optionId = prop.options.get(value.toLowerCase())
  if (!optionId) {
    optionId = uid()
    prop.options.set(value.toLowerCase(), optionId)
    commands.push({ type: 'property.addOption', key: prop.key, option: { id: optionId, value } })
  }
  return { key: prop.key, optionId }
}

/** Map a subject's free-form variantProperties ("mood: angry") onto library
 * select properties + facet values on its imported assets — and, when the
 * import carries reference angles (`withView`), facet every image with a
 * View property (Main / Angle) so mains and angles stay distinguishable in
 * Matrix/Table. Existing property labels/options are reused; new ones are
 * created inside the same batch. */
function appendVariantPropertyCommands(
  subject: SubjectCanvasNode,
  assets: { id: string; role: 'main' | 'angle' }[],
  commands: LibraryCommand[],
  plan: PropertyPlan,
  withView: boolean,
): void {
  if (!assets.length) return
  for (const [rawLabel, rawValue] of Object.entries(subject.variantProperties ?? {})) {
    const label = rawLabel.trim()
    const value = rawValue.trim()
    if (!label || !value) continue
    const { key, optionId } = planOption(label, value, commands, plan)
    for (const asset of assets) {
      commands.push({ type: 'asset.setValue', id: asset.id, key, value: optionId })
    }
  }
  if (!withView) return
  for (const asset of assets) {
    const { key, optionId } = planOption('View', asset.role === 'main' ? 'Main' : 'Angle', commands, plan)
    commands.push({ type: 'asset.setValue', id: asset.id, key, value: optionId })
  }
}

/** Convert one old subject into the library (new collection, or file its
 * images into an existing one). One atomic batch = one undo step. */
export type ImportSubjectsResult =
  | { ok: true; collectionIds: string[]; imageCount: number; skipped: number }
  | { ok: false; reason: 'no-images' | 'access-denied' }

/** Convert several old subjects into the library in ONE atomic batch = one
 * dispatch = one undo step (a later subject failing can't leave a partial
 * import). Each subject lands in its own new collection, or all file into
 * `intoCollectionId` — sharing one dedup set so duplicate URLs converge. */
export function importSubjectsToLibrary(
  subjects: SubjectCanvasNode[],
  opts: { intoCollectionId?: string; enableForProjectId?: string } = {},
): ImportSubjectsResult {
  const s = useLibraryStore.getState()
  // Same refusal set as saveCanvasAssetsToLibrary above — a guest's dispatch
  // is dropped by the store, so returning ok here would show "added to
  // Library" for rows the server never sees.
  if (selectLibraryWriteAccess(s) !== 'writable') {
    return { ok: false, reason: 'access-denied' }
  }
  const commands: LibraryCommand[] = []
  const takenSlugs = s.collections.map(c => c.slug)
  const propertyPlan = initPropertyPlan()
  const sharedDedup = opts.intoCollectionId ? storeSrcsFor(opts.intoCollectionId) : undefined
  // Filing several subjects into one collection: View faceting is an
  // all-or-nothing decision so the facet has no blanks (same rule as groups).
  const sharedWithView = opts.intoCollectionId ? subjects.some(sub => subjectImageEntries(sub).length > 1) : false
  const collectionIds: string[] = []
  let imageCount = 0
  let createdCount = 0
  let skipped = 0
  for (const subject of subjects) {
    const plan = buildSubjectImportCommands(
      subject,
      { intoCollectionId: opts.intoCollectionId, dedupSrcs: sharedDedup ?? new Set() },
      commands,
      takenSlugs,
    )
    if (!plan) {
      skipped++
      continue
    }
    collectionIds.push(plan.collectionId)
    imageCount += plan.imageCount
    if (plan.created) createdCount++
    appendVariantPropertyCommands(
      subject,
      plan.assets,
      commands,
      propertyPlan,
      opts.intoCollectionId ? sharedWithView : subjectImageEntries(subject).length > 1,
    )
  }
  if (!collectionIds.length) return { ok: false, reason: 'no-images' }
  const uniqueIds = [...new Set(collectionIds)]
  const trackEnable = opts.enableForProjectId ? appendEnableCommand(opts.enableForProjectId, uniqueIds, commands) : null
  if (commands.length) {
    const label = subjects.length > 1 ? 'Add subjects to library' : 'Add subject to library'
    s.dispatch({ type: 'batch', label, commands }, { label })
    trackEnable?.()
  }
  trackLibraryAssetsAdded({
    workspaceId: s.workspaceId,
    source: 'subject_migration',
    count: imageCount,
    imageCount,
    videoCount: 0,
    textCount: 0,
    assetSourceKind: 'import',
  })
  for (let i = 0; i < createdCount; i++) {
    trackLibraryCollectionCreated({ workspaceId: s.workspaceId, source: 'subject_migration' })
  }
  return { ok: true, collectionIds: uniqueIds, imageCount, skipped }
}

export function importSubjectToLibrary(
  subject: SubjectCanvasNode,
  opts: { intoCollectionId?: string; enableForProjectId?: string } = {},
): ImportSubjectResult {
  const result = importSubjectsToLibrary([subject], opts)
  if (!result.ok) return result
  return { ok: true, collectionId: result.collectionIds[0], imageCount: result.imageCount }
}

export type ImportGroupResult =
  | { ok: true; collectionId: string; imageCount: number; skipped: number }
  | { ok: false; reason: 'no-images' | 'access-denied' }

/** Convert an old subject group into ONE library collection: each member
 * subject's images become variants inside it, and members' variantProperties
 * become select properties + facet values (so Matrix/Table views work).
 * A live collection with the group's name is merged into (re-import
 * converges); per-URL dedup makes that cheap. */
export function importSubjectGroupToLibrary(
  groupName: string,
  subjects: SubjectCanvasNode[],
  opts: { enableForProjectId?: string } = {},
): ImportGroupResult {
  const s = useLibraryStore.getState()
  // Same refusal set as saveCanvasAssetsToLibrary above — a guest's dispatch
  // is dropped by the store, so returning ok here would show "added to
  // Library" for rows the server never sees.
  if (selectLibraryWriteAccess(s) !== 'writable') {
    return { ok: false, reason: 'access-denied' }
  }
  const name = groupName.trim() || 'Collection'
  const now = Date.now()
  const commands: LibraryCommand[] = []

  const existing = s.collections.find(c => !c.deletedAt && c.name.trim().toLowerCase() === name.toLowerCase())
  let target = existing?.id
  const created = !target
  if (!target) {
    target = uid()
    const categories = [...new Set(subjects.map(sub => sub.category.trim()).filter(Boolean))]
    commands.push({
      type: 'collection.create',
      collection: {
        id: target,
        name,
        slug: toSlug(
          name,
          s.collections.map(c => c.slug),
        ),
        folder: null,
        tags: categories,
        heroAssetId: null, // UI falls back to the first live asset = first member's main image
        usedIn: 0,
        rev: 1,
        createdAt: now,
        updatedAt: now,
      },
    })
    const newTags = categories.filter(c => !s.tagVocab.includes(c))
    if (newTags.length) commands.push({ type: 'tag.addToVocab', tags: newTags })
  }

  const propertyPlan = initPropertyPlan()
  // View faceting is a group-wide decision: if ANY member brings reference
  // angles, every image gets a View value so the facet has no blanks.
  const withView = subjects.some(sub => subjectImageEntries(sub).length > 1)
  // One dedup set for the whole batch — the store can't see earlier planned
  // assets, so members sharing an image URL must converge here.
  const dedupSrcs = storeSrcsFor(target)
  let imageCount = 0
  let skipped = 0
  for (const subject of subjects) {
    const plan = buildSubjectImportCommands(subject, { intoCollectionId: target, dedupSrcs }, commands, [])
    if (!plan) {
      skipped++
      continue
    }
    imageCount += plan.imageCount
    appendVariantPropertyCommands(subject, plan.assets, commands, propertyPlan, withView)
  }
  if (!imageCount && !existing) return { ok: false, reason: 'no-images' }
  const trackEnable = opts.enableForProjectId ? appendEnableCommand(opts.enableForProjectId, [target], commands) : null
  if (commands.length) {
    useLibraryStore
      .getState()
      .dispatch({ type: 'batch', label: 'Add collection to library', commands }, { label: 'Add collection to library' })
    trackEnable?.()
  }
  trackLibraryAssetsAdded({
    workspaceId: s.workspaceId,
    source: 'subject_migration',
    count: imageCount,
    imageCount,
    videoCount: 0,
    textCount: 0,
    assetSourceKind: 'import',
  })
  if (created) trackLibraryCollectionCreated({ workspaceId: s.workspaceId, source: 'subject_migration' })
  return { ok: true, collectionId: target, imageCount, skipped }
}

/** One-click group import for the canvas context menu: import, enable the
 * landed collection for the current project, surface the saved toast. */
export type ImportGroupSplitResult =
  | { ok: true; folder: string; collectionIds: string[]; imageCount: number; skipped: number }
  | { ok: false; reason: 'no-images' | 'access-denied' }

/** The alternative group mapping: one collection PER member subject, filed
 * together in a folder named after the group. For groups whose members are
 * different entities (a cast) rather than variants of one thing. Members
 * whose name already has a live collection in that folder merge into it. */
export function importSubjectGroupAsCollections(
  groupName: string,
  subjects: SubjectCanvasNode[],
  opts: { enableForProjectId?: string } = {},
): ImportGroupSplitResult {
  const s = useLibraryStore.getState()
  // Same refusal set as saveCanvasAssetsToLibrary above — a guest's dispatch
  // is dropped by the store, so returning ok here would show "added to
  // Library" for rows the server never sees.
  if (selectLibraryWriteAccess(s) !== 'writable') {
    return { ok: false, reason: 'access-denied' }
  }
  const folder = (groupName.trim() || 'Collection').replace(/\//g, '-')
  const commands: LibraryCommand[] = [{ type: 'folder.create', path: folder }]
  const takenSlugs = s.collections.map(c => c.slug)
  const propertyPlan = initPropertyPlan()
  // Batch-local state the store can't see yet: same-named members must land
  // in ONE collection (not mint duplicates), and each target keeps one dedup
  // set so members sharing an image URL converge.
  const createdByName = new Map<string, string>()
  const dedupByTarget = new Map<string, Set<string>>()
  const collectionIds: string[] = []
  let imageCount = 0
  let createdCount = 0
  let skipped = 0
  for (const subject of subjects) {
    const nameKey = subject.name.trim().toLowerCase()
    const existing = s.collections.find(
      c => !c.deletedAt && c.folder === folder && c.name.trim().toLowerCase() === nameKey,
    )
    const target = existing?.id ?? createdByName.get(nameKey)
    let dedupSrcs = target ? dedupByTarget.get(target) : undefined
    if (!dedupSrcs) dedupSrcs = target ? storeSrcsFor(target) : new Set()
    const plan = buildSubjectImportCommands(
      subject,
      { intoCollectionId: target, folder, dedupSrcs },
      commands,
      takenSlugs,
    )
    if (!plan) {
      skipped++
      continue
    }
    dedupByTarget.set(plan.collectionId, dedupSrcs)
    if (plan.created) {
      createdByName.set(nameKey, plan.collectionId)
      createdCount++
    }
    collectionIds.push(plan.collectionId)
    imageCount += plan.imageCount
    appendVariantPropertyCommands(subject, plan.assets, commands, propertyPlan, subjectImageEntries(subject).length > 1)
  }
  if (!collectionIds.length) return { ok: false, reason: 'no-images' }
  const uniqueIds = [...new Set(collectionIds)]
  const trackEnable = opts.enableForProjectId ? appendEnableCommand(opts.enableForProjectId, uniqueIds, commands) : null
  useLibraryStore
    .getState()
    .dispatch({ type: 'batch', label: 'Add collection to library', commands }, { label: 'Add collection to library' })
  trackEnable?.()
  trackLibraryAssetsAdded({
    workspaceId: s.workspaceId,
    source: 'subject_migration',
    count: imageCount,
    imageCount,
    videoCount: 0,
    textCount: 0,
    assetSourceKind: 'import',
  })
  for (let i = 0; i < createdCount; i++) {
    trackLibraryCollectionCreated({ workspaceId: s.workspaceId, source: 'subject_migration' })
  }
  return { ok: true, folder, collectionIds: uniqueIds, imageCount, skipped }
}

export type GroupImportMode = 'one-collection' | 'collection-per-subject'

/** One-click group import for the canvas context menu: import in the chosen
 * mapping, enable the landed collection(s) for the current project, surface
 * the saved toast. */
export function importSubjectGroupFromCanvas(
  groupName: string,
  subjects: SubjectCanvasNode[],
  mode: GroupImportMode = 'one-collection',
): boolean {
  // Project links ride inside the import batch — one undo reverses everything.
  const projectId = useBootstrapStore.getState().currentProjectId ?? undefined
  const result =
    mode === 'collection-per-subject'
      ? importSubjectGroupAsCollections(groupName, subjects, { enableForProjectId: projectId })
      : importSubjectGroupToLibrary(groupName, subjects, { enableForProjectId: projectId })
  if (!result.ok) return false
  emitLibrarySavedNotice()
  return true
}
